import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, UserSearch, DollarSign, BarChart3, Calendar, Bell, FileText, StickyNote } from 'lucide-react';

const features = [
  {
    icon: Building2,
    title: 'Properties & Units Management',
    description: 'Combined view for properties and their units on a single page.',
    details: [
      'Properties listed as expandable cards with summary info always visible',
      'Summary shows: property name, address, unit count badge, and unit number badges with sizes',
      'Expandable "Property Details" section: owner/manager name, phone, email, parking, pets toggle with notes, amenities tags, additional notes',
      'Expandable "Units" section: table of all units with full CRUD (add, edit, delete)',
      'Unit fields: number, size (0/1, 1/1, 2/1, 2/2, 3/1, 3/2, 3/3, or custom Other), base rent, additional monthly costs (repeatable), availability start date, optional close date',
      'Cannot delete property with existing units; cannot delete unit with existing tenants',
      'Create, edit, and delete both properties and units from the same page'
    ]
  },
  {
    icon: Users,
    title: 'Tenants Management',
    description: 'Redesigned tenant view with current, future, and past sections plus move-out confirmation flow.',
    details: [
      'Top section: Current tenants grouped by property and unit',
      'Each unit has a dropdown to view future tenants in date order',
      'Pending move-out tenants (past move-out date, not yet confirmed) shown with amber badge and Confirm button',
      'Auto-popup notification when a tenant\'s move-out date passes to confirm move-out',
      'On move-out confirmation: long-term tenants with deposits get a deposit return reminder notification 3 days later',
      'Bottom section: Past tenants shown via collapsible dropdown, divided by property and unit',
      'Click on any tenant row to see full detail dialog with all available info',
      'Airbnb/VRBO toggle changes form: long-term shows deposit, monthly rent, payment method, rent due date, pets, parking, notes',
      'Airbnb/VRBO shows total rent, auto-calculated nights/rate/monthly breakdown, and notes field',
      'Date validation: no overlapping tenants, move-in >= unit availability, move-out <= close date',
      'Same-day turnover allowed (move-out date can equal next move-in)',
      'Airbnb monthly breakdown stored and feeds into Rent Roll page'
    ]
  },
  {
    icon: UserSearch,
    title: 'Leads Tracking',
    description: 'Track prospective tenants grouped by property with an 8-stage pipeline.',
    details: [
      'Leads grouped by property based on selected potential units',
      'If a lead has units across multiple properties, it appears under each property',
      'Unassigned Leads section at top for leads with no available units — click to add notes about what the lead is useful for',
      'Lead fields: name, source, phone, email, desired dates, price offered, preferred contact method, notes',
      'Potential units (multi-select filtered by availability during selected dates)',
      'When no units available for dates, option to save as unassigned lead',
      'Pets and parking request fields',
      'Lead strength (4 levels): Weak (red), Fair (orange), Good (yellow), Strong (green)',
      'Color-coded rows with left border bar based on strength',
      '8 progress stages: Contacted, Showing Set, Showing Complete, BG Check Submitted, BG Check Complete, Lease Sent, Lease Signed, Deposit Submitted',
      'Showing Set stage requires date and time input',
      'Each stage advancement triggers notification creation prompt',
      'At Deposit Submitted (stage 8): "Convert to Tenant" button pre-fills tenant form'
    ]
  },
  {
    icon: DollarSign,
    title: 'Rent Roll',
    description: 'Dynamically generated income from tenant data.',
    details: [
      'Current month total income displayed prominently',
      'Yearly total and monthly average KPI cards',
      'Year navigation with arrow buttons',
      'Expandable monthly breakdown: Month -> Property -> Unit -> Tenant',
      'Long-term tenants: monthly_rent + additional_monthly_costs',
      'Partial first/last month overrides supported',
      'Airbnb tenants: income distributed by nights per month',
      'Deposits excluded from income totals',
      'Additional monthly costs included in income totals'
    ]
  },
  {
    icon: BarChart3,
    title: 'Vacancy Page',
    description: 'Vacancy analytics with multiple breakdown views.',
    details: [
      'By Building (Monthly): vacancy %, total vacant days for each property',
      'Expandable to see per-unit vacancy within each month',
      'By Unit Size: grouped across all buildings, showing monthly vacancy %',
      'Upcoming Vacancies (next 3 months): property, unit, vacancy start date',
      'Shows "Vacant from [date] forward" if no future tenant exists',
      'Same-day turnover (move-out = next move-in) counts as 0 vacancy days',
      'Year navigation supported'
    ]
  },
  {
    icon: Calendar,
    title: 'Occupancy Timeline (Calendar)',
    description: 'Horizontal, scrollable occupancy timeline inspired by Airbnb host calendars.',
    details: [
      'Continuous horizontal timeline divided visually by month (no individual day squares)',
      'Units stacked vertically, grouped by property with clear headers',
      'Confirmed tenants shown as solid proportional bars (green for long-term, blue for Airbnb/VRBO)',
      'Vacant periods shown as light gray background',
      'Lead interest shown as striped/dashed overlay on vacant dates only — booking always overrides lead visually',
      'Hover tooltip shows tenant/lead name, dates, and rent amount',
      'Click on booking navigates to tenant page; click on lead navigates to leads page',
      'Sticky left column with property and unit names, sticky month header row',
      '"Today" red marker line for quick orientation',
      'Property selector dropdown to filter by a specific property',
      'Scroll navigation buttons (left, right, Today) for quick timeline movement',
      'Date math powered by date-fns for accurate month-length handling',
      'Responsive: desktop priority, tablet functional'
    ]
  },
  {
    icon: Bell,
    title: 'Notifications',
    description: 'In-app notification system triggered from lead stage changes.',
    details: [
      'Bell icon in top bar with unread count badge',
      'Side panel (Sheet) opens with Active and Viewed sections',
      'Each notification shows lead name, stage, message, and due date',
      'Mark as read/unread functionality',
      'Delete individual notifications',
      'Created when advancing lead through stages (optional per stage)',
      'Auto-refreshes every 30 seconds'
    ]
  },
  {
    icon: StickyNote,
    title: 'Notes',
    description: 'General-purpose notes for quick reference and reminders.',
    details: [
      'Create, view, edit, and delete notes',
      'Each note has a title, content body, and optional color tag',
      'Color options: default (neutral), yellow, green, blue, pink',
      'Notes displayed as a card grid for quick visual scanning',
      'Sorted by most recently updated',
      'Useful for property reminders, maintenance notes, or general to-dos'
    ]
  },
  {
    icon: FileText,
    title: 'Features Reference (This Page)',
    description: 'Complete documentation of all system features.',
    details: [
      'Lists every feature built into the system',
      'Includes field descriptions and validation rules',
      'Useful as a reference when making updates or edits'
    ]
  }
];

export default function FeaturesPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Features Reference</h1>
        <p className="text-sm text-muted-foreground mt-1">Complete documentation of all system features for reference</p>
      </div>

      <div className="space-y-4">
        {features.map((feature, idx) => (
          <Card key={idx}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center">
                  <feature.icon className="h-5 w-5 text-accent-foreground" />
                </div>
                <div>
                  <CardTitle className="text-base font-semibold">{feature.title}</CardTitle>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5">
                {feature.details.map((detail, i) => (
                  <li key={i} className="text-sm text-foreground/80 flex items-start gap-2">
                    <span className="text-primary mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                    {detail}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">System Rules</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 flex-shrink-0">Validation</Badge>
              <span>No overlapping tenants in the same unit</span>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 flex-shrink-0">Validation</Badge>
              <span>Move-in must be after unit availability date</span>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 flex-shrink-0">Calculation</Badge>
              <span>Airbnb checkout day is not a billable night</span>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 flex-shrink-0">Calculation</Badge>
              <span>Same-day turnover = 0 vacancy days</span>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 flex-shrink-0">Rent Roll</Badge>
              <span>Deposits excluded from income totals</span>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="mt-0.5 flex-shrink-0">Rent Roll</Badge>
              <span>Additional monthly costs included in income</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
